import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';

import { fmtDay, fmtDayShort } from '@/lib/datetime';
import {
  PERF_HALO_METRIC_IDS,
  type PerfHaloEffect,
  type PerfHaloMetricId,
} from '@/lib/perf-halo-metrics';
import type { PerfHaloTrendPoint } from '@/lib/perf-halo-trend';

import './perf-halo-trend-chart.css';

export interface PerfHaloTrendChartProps {
  points: ReadonlyArray<PerfHaloTrendPoint>;
  metric: PerfHaloMetricId;
  ariaLabel?: string;
  className?: string;
}

interface MetricDefinition {
  label: string;
  shortLabel: string;
  value: (point: PerfHaloTrendPoint, effect: PerfHaloEffect) => number | null;
  format: (value: number, compact?: boolean) => string;
  breakdown: boolean;
}

export interface PerfHaloTrendAxisScale {
  min: number;
  max: number;
  ticks: readonly number[];
}

interface SeriesDefinition {
  effect: PerfHaloEffect;
  label: string;
  values: ReadonlyArray<number | null>;
}

const integerFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});
const decimalFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const preciseFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 6,
});
const compactNumberFormat = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compactCurrencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const HUNDRED_DOLLARS_IN_CENTS = 10_000;
const MAX_Y_AXIS_INTERVALS = 6;

/**
 * Builds an outward-rounded currency scale for absolute dollar totals.
 *
 * Trend values are stored in cents. Every bound, tick, and tick interval is
 * therefore an exact multiple of $100. The interval may grow by whole $100
 * units when necessary so the chart retains at most seven readable labels.
 */
export function perfHaloHundredDollarAxis(
  values: ReadonlyArray<number>,
): PerfHaloTrendAxisScale {
  const finiteValues = values.filter(Number.isFinite);
  const rawMin = finiteValues.length ? Math.min(0, ...finiteValues) : 0;
  const rawMax = finiteValues.length ? Math.max(0, ...finiteValues) : 0;

  if (rawMin === 0 && rawMax === 0) {
    return {
      min: 0,
      max: HUNDRED_DOLLARS_IN_CENTS,
      ticks: [0, HUNDRED_DOLLARS_IN_CENTS],
    };
  }

  const spanInHundreds = (rawMax - rawMin) / HUNDRED_DOLLARS_IN_CENTS;
  let stepInHundreds = Math.max(
    1,
    Math.ceil(spanInHundreds / MAX_Y_AXIS_INTERVALS),
  );

  for (;;) {
    const step = stepInHundreds * HUNDRED_DOLLARS_IN_CENTS;
    const min = Math.floor(rawMin / step) * step;
    const max = Math.ceil(rawMax / step) * step;
    const intervals = Math.round((max - min) / step);

    if (intervals <= MAX_Y_AXIS_INTERVALS) {
      return {
        min: Object.is(min, -0) ? 0 : min,
        max,
        ticks: Array.from(
          { length: intervals + 1 },
          (_, index) => min + step * index,
        ),
      };
    }

    // Rounding both signed bounds outward can add an interval. Increase the
    // step proportionally instead of walking one $100 unit at a time.
    stepInHundreds = Math.max(
      stepInHundreds + 1,
      Math.ceil(stepInHundreds * intervals / MAX_Y_AXIS_INTERVALS),
    );
  }
}

function finite(value: number): number | null {
  return Number.isFinite(value) ? value : null;
}

function ratio(numerator: number, denominator: number, multiplier = 1): number | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return null;
  }
  return numerator / denominator * multiplier;
}

function deliveryValue(
  point: PerfHaloTrendPoint,
  value: number | null,
): number | null {
  return point.deliveryAvailable && value !== null ? finite(value) : null;
}

function attributionValue(
  point: PerfHaloTrendPoint,
  value: number | null,
): number | null {
  return point.attributionAvailable && value !== null ? finite(value) : null;
}

function combinedValue(
  point: PerfHaloTrendPoint,
  value: number | null,
): number | null {
  if (!point.deliveryAvailable || !point.attributionAvailable) return null;
  return value === null ? null : finite(value);
}

function effectTickets(point: PerfHaloTrendPoint, effect: PerfHaloEffect): number {
  if (effect === 'direct') return point.directTickets;
  if (effect === 'halo') return point.haloTickets;
  return point.totalTickets;
}

function effectTicketValueCents(
  point: PerfHaloTrendPoint,
  effect: PerfHaloEffect,
): number {
  if (effect === 'direct') return point.directTicketValueCents;
  if (effect === 'halo') return point.haloTicketValueCents;
  return point.totalTicketValueCents;
}

function formatCount(value: number, compact = false): string {
  if (compact) return compactNumberFormat.format(value);
  return Math.abs(value - Math.round(value)) < 0.000001
    ? integerFormat.format(value)
    : preciseFormat.format(value);
}

function formatCurrencyCents(value: number, compact = false): string {
  return (compact ? compactCurrencyFormat : currencyFormat).format(value / 100);
}

function formatPercent(value: number): string {
  return `${decimalFormat.format(value)}%`;
}

function formatRoas(value: number): string {
  return `${decimalFormat.format(value)}×`;
}

const METRICS: Record<PerfHaloMetricId, MetricDefinition> = {
  gross_spend: {
    label: 'Gross spend',
    shortLabel: 'Spend',
    value: (point) => deliveryValue(point, point.grossSpendCents),
    format: formatCurrencyCents,
    breakdown: false,
  },
  impressions: {
    label: 'Impressions',
    shortLabel: 'Impressions',
    value: (point) => deliveryValue(point, point.impressions),
    format: formatCount,
    breakdown: false,
  },
  clicks: {
    label: 'Clicks',
    shortLabel: 'Clicks',
    value: (point) => deliveryValue(point, point.clicks),
    format: formatCount,
    breakdown: false,
  },
  ctr: {
    label: 'Click-through rate',
    shortLabel: 'CTR',
    value: (point) => deliveryValue(
      point,
      ratio(point.clicks, point.impressions, 100),
    ),
    format: formatPercent,
    breakdown: false,
  },
  cpm: {
    label: 'Cost per thousand impressions',
    shortLabel: 'CPM',
    value: (point) => deliveryValue(
      point,
      ratio(point.grossSpendCents, point.impressions, 1_000),
    ),
    format: formatCurrencyCents,
    breakdown: false,
  },
  cpc: {
    label: 'Cost per click',
    shortLabel: 'CPC',
    value: (point) => deliveryValue(
      point,
      ratio(point.grossSpendCents, point.clicks),
    ),
    format: formatCurrencyCents,
    breakdown: false,
  },
  attributed_conversions: {
    label: 'Tickets',
    shortLabel: 'Tickets',
    value: (point, effect) => attributionValue(point, effectTickets(point, effect)),
    format: formatCount,
    breakdown: true,
  },
  attributed_revenue: {
    label: 'Ticket Value',
    shortLabel: 'Ticket Value',
    value: (point, effect) => attributionValue(
      point,
      effectTicketValueCents(point, effect),
    ),
    format: formatCurrencyCents,
    breakdown: true,
  },
  cpa: {
    label: 'Cost per ticket',
    shortLabel: 'CPA',
    value: (point) => combinedValue(
      point,
      point.attributionGrossSpendCents === null
        ? null
        : ratio(point.attributionGrossSpendCents, point.totalTickets),
    ),
    format: formatCurrencyCents,
    breakdown: false,
  },
  roas: {
    label: 'Return on ad spend',
    shortLabel: 'ROAS',
    value: (point, effect) => combinedValue(
      point,
      point.attributionGrossSpendCents === null
        ? null
        : ratio(
            effectTicketValueCents(point, effect),
            point.attributionGrossSpendCents,
          ),
    ),
    format: formatRoas,
    breakdown: true,
  },
};

function shortDate(date: string, includesMultipleYears: boolean): string {
  return includesMultipleYears ? fmtDay(date) : fmtDayShort(date);
}

/** Responsive sparse ticks; first and last calendar days are invariant. */
export function perfHaloTrendTickIndexes(count: number, width: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const tickCount = Math.min(count, Math.max(2, Math.min(7, Math.floor(width / 105))));
  const indexes = new Set<number>([0, count - 1]);
  for (let tick = 1; tick < tickCount - 1; tick += 1) {
    indexes.add(Math.round(tick * (count - 1) / (tickCount - 1)));
  }
  return [...indexes].sort((left, right) => left - right);
}

function lineSegments(
  values: ReadonlyArray<number | null>,
  x: (index: number) => number,
  y: (value: number) => number,
): Array<Array<readonly [number, number]>> {
  const result: Array<Array<readonly [number, number]>> = [];
  let segment: Array<readonly [number, number]> = [];
  values.forEach((value, index) => {
    if (value === null) {
      if (segment.length) result.push(segment);
      segment = [];
      return;
    }
    segment.push([x(index), y(value)]);
  });
  if (segment.length) result.push(segment);
  return result;
}

function useChartWidth() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(760);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const measure = () => {
      const next = Math.round(container.getBoundingClientRect().width);
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return { containerRef, width };
}

function findNextAvailable(
  values: ReadonlyArray<number | null>,
  from: number,
  direction: -1 | 1,
): number {
  for (
    let index = from + direction;
    index >= 0 && index < values.length;
    index += direction
  ) {
    if (values[index] !== null) return index;
  }
  return from;
}

function findBoundaryAvailable(
  values: ReadonlyArray<number | null>,
  direction: -1 | 1,
): number | null {
  let index = direction === 1 ? 0 : values.length - 1;
  while (index >= 0 && index < values.length) {
    if (values[index] !== null) return index;
    index += direction;
  }
  return null;
}

function metricSeries(
  definition: MetricDefinition,
  points: readonly PerfHaloTrendPoint[],
): SeriesDefinition[] {
  const effects: readonly PerfHaloEffect[] = definition.breakdown
    ? ['total', 'direct', 'halo']
    : ['total'];
  return effects.map((effect) => ({
    effect,
    label: effect === 'total' ? 'Total' : effect === 'direct' ? 'Direct' : 'Halo',
    values: points.map((point) => definition.value(point, effect)),
  }));
}

function TooltipMetric({
  metric,
  point,
  selected,
}: {
  metric: PerfHaloMetricId;
  point: PerfHaloTrendPoint;
  selected: boolean;
}) {
  const definition = METRICS[metric];
  const total = definition.value(point, 'total');
  const direct = definition.breakdown ? definition.value(point, 'direct') : null;
  const halo = definition.breakdown ? definition.value(point, 'halo') : null;
  return (
    <div
      className={`perf-halo-trend-chart__tooltip-metric${selected ? ' is-selected' : ''}`}
      data-metric={metric}
      data-selected={selected ? 'true' : 'false'}
    >
      <div className="perf-halo-trend-chart__tooltip-total">
        <span>{definition.shortLabel}</span>
        <strong>{total === null ? 'Unavailable' : definition.format(total)}</strong>
      </div>
      {definition.breakdown && total !== null && (
        <div className="perf-halo-trend-chart__tooltip-breakdown">
          <span>Direct {direct === null ? '—' : definition.format(direct)}</span>
          <span>Halo {halo === null ? '—' : definition.format(halo)}</span>
        </div>
      )}
    </div>
  );
}

export function PerfHaloTrendChart({
  points,
  metric,
  ariaLabel = 'Perf + Halo trend',
  className = '',
}: PerfHaloTrendChartProps) {
  const titleId = useId();
  const descriptionId = useId();
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLElement>(null);
  const { containerRef, width } = useChartWidth();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const orderedPoints = useMemo(
    () => [...points].sort((left, right) => left.date.localeCompare(right.date)),
    [points],
  );
  const definition = METRICS[metric];
  const series = useMemo(
    () => metricSeries(definition, orderedPoints),
    [definition, orderedPoints],
  );
  const primaryValues = series[0]?.values ?? [];

  useEffect(() => {
    setActiveIndex((current) => (
      current !== null
      && primaryValues[current] !== undefined
      && primaryValues[current] !== null
        ? current
        : null
    ));
  }, [primaryValues]);

  const height = 260;
  const pad = { top: 20, right: 18, bottom: 42, left: width < 500 ? 48 : 58 };
  const innerWidth = Math.max(width - pad.left - pad.right, 1);
  const innerHeight = height - pad.top - pad.bottom;
  const finiteValues = series
    .flatMap((item) => item.values)
    .filter((value): value is number => value !== null);
  const usesHundredDollarAxis = metric === 'gross_spend'
    || metric === 'attributed_revenue';
  const currencyAxis = usesHundredDollarAxis
    ? perfHaloHundredDollarAxis(finiteValues)
    : null;
  const rawMin = finiteValues.length ? Math.min(0, ...finiteValues) : 0;
  const rawMax = finiteValues.length ? Math.max(0, ...finiteValues) : 1;
  const rawSpan = Math.max(rawMax - rawMin, 1);
  let min = currencyAxis?.min ?? (rawMin < 0 ? rawMin - rawSpan * 0.1 : 0);
  let max = currencyAxis?.max ?? (rawMax > 0 ? rawMax + rawSpan * 0.1 : 0);
  if (min === max) {
    min = 0;
    max = 1;
  }
  const span = max - min;
  const yAxisTicks = currencyAxis
    ? [...currencyAxis.ticks].reverse()
    : Array.from({ length: 5 }, (_, index) => max - span * index / 4);
  const step = innerWidth / Math.max(orderedPoints.length - 1, 1);
  const x = (index: number) => orderedPoints.length === 1
    ? pad.left + innerWidth / 2
    : pad.left + index * step;
  const y = (value: number) => pad.top + (max - value) / span * innerHeight;
  const zeroY = y(0);
  const tickIndexes = perfHaloTrendTickIndexes(orderedPoints.length, width);
  const firstYear = orderedPoints[0]?.date.slice(0, 4);
  const lastYear = orderedPoints[orderedPoints.length - 1]?.date.slice(0, 4);
  const includesMultipleYears = firstYear !== lastYear;
  const activePoint = activeIndex === null ? null : orderedPoints[activeIndex] ?? null;
  const activeValue = activeIndex === null ? null : primaryValues[activeIndex] ?? null;

  const selectPoint = (index: number) => {
    if (primaryValues[index] !== null && primaryValues[index] !== undefined) {
      setActiveIndex(index);
    } else {
      // Every calendar-day band remains pointer-addressable so gaps are easy
      // to discover. Entering one must clear the prior day's tooltip instead
      // of leaving stale values attached to a different date.
      setActiveIndex(null);
    }
  };

  const handleKeyDown = (
    event: ReactKeyboardEvent<SVGGElement>,
    index: number,
  ) => {
    let next: number | null = null;
    if (event.key === 'ArrowLeft') next = findNextAvailable(primaryValues, index, -1);
    if (event.key === 'ArrowRight') next = findNextAvailable(primaryValues, index, 1);
    if (event.key === 'Home') next = findBoundaryAvailable(primaryValues, 1);
    if (event.key === 'End') next = findBoundaryAvailable(primaryValues, -1);
    if (event.key === 'Escape') {
      setActiveIndex(null);
      event.currentTarget.blur();
      event.preventDefault();
      return;
    }
    if (next === null) return;
    event.preventDefault();
    const target = wrapperRef.current?.querySelector<SVGGElement>(
      `[data-perf-halo-point-index="${next}"]`,
    );
    target?.focus();
    selectPoint(next);
  };

  const handlePointerLeave = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return;
    if (wrapperRef.current?.contains(document.activeElement)) return;
    setActiveIndex(null);
  };

  const handleBlur = (event: ReactFocusEvent<HTMLElement>) => {
    if (event.relatedTarget && wrapperRef.current?.contains(event.relatedTarget as Node)) return;
    setActiveIndex(null);
  };

  const tooltipStyle = activeIndex === null
    ? undefined
    : ({
        '--perf-halo-tooltip-x': `${x(activeIndex) / width * 100}%`,
      } as CSSProperties);

  const selectedAriaValue = (point: PerfHaloTrendPoint) => {
    const total = definition.value(point, 'total');
    if (total === null) return `${definition.label} unavailable.`;
    if (!definition.breakdown) return `${definition.label}: ${definition.format(total)}.`;
    const direct = definition.value(point, 'direct');
    const halo = definition.value(point, 'halo');
    return `${definition.label}: Total ${definition.format(total)}, Direct ${definition.format(direct ?? 0)}, Halo ${definition.format(halo ?? 0)}.`;
  };

  return (
    <figure
      ref={wrapperRef}
      className={`perf-halo-trend-chart ${className}`.trim()}
      aria-label={ariaLabel}
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
    >
      {definition.breakdown && (
        <div className="perf-halo-trend-chart__legend" aria-label={`${definition.label} series`}>
          {series.map((item) => (
            <span key={item.effect} data-series-legend={item.effect}>
              <i aria-hidden="true"/>{item.label}
            </span>
          ))}
        </div>
      )}
      <div className="perf-halo-trend-chart__surface" ref={containerRef}>
        <svg
          className="perf-halo-trend-chart__svg"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="group"
          aria-labelledby={`${titleId} ${descriptionId}`}
          preserveAspectRatio="none"
        >
          <title id={titleId}>{definition.label} over time</title>
          <desc id={descriptionId}>
            ET calendar-day trend. Unpublished days are gaps. Use Tab and arrow keys to inspect published points.
          </desc>

          <g className="perf-halo-trend-chart__grid" aria-hidden="true">
            {yAxisTicks.map((tickValue) => {
              const tickY = y(tickValue);
              return (
                <g key={tickValue} data-y-axis-value={tickValue}>
                  <line x1={pad.left} x2={width - pad.right} y1={tickY} y2={tickY}/>
                  <text x={pad.left - 8} y={tickY + 3} textAnchor="end">
                    {definition.format(tickValue, true)}
                  </text>
                </g>
              );
            })}
          </g>

          {min < 0 && max > 0 && (
            <line
              className="perf-halo-trend-chart__zero-line"
              x1={pad.left}
              x2={width - pad.right}
              y1={zeroY}
              y2={zeroY}
              aria-hidden="true"
            />
          )}

          <g className="perf-halo-trend-chart__x-axis" aria-hidden="true">
            {tickIndexes.map((index) => (
              <g key={orderedPoints[index]!.date}>
                <line x1={x(index)} x2={x(index)} y1={height - pad.bottom} y2={height - pad.bottom + 5}/>
                <text
                  x={x(index)}
                  y={height - 16}
                  textAnchor={orderedPoints.length === 1
                    ? 'middle'
                    : index === 0
                      ? 'start'
                      : index === orderedPoints.length - 1
                        ? 'end'
                        : 'middle'}
                >
                  {shortDate(orderedPoints[index]!.date, includesMultipleYears)}
                </text>
              </g>
            ))}
          </g>

          <g className="perf-halo-trend-chart__series" aria-hidden="true">
            {series.map((item) => (
              <g key={item.effect} data-series={item.effect}>
                {lineSegments(item.values, x, y).map((segment, segmentIndex) => {
                  const path = segment.map(([pointX, pointY], pointIndex) => (
                    `${pointIndex === 0 ? 'M' : 'L'}${pointX},${pointY}`
                  )).join(' ');
                  if (segment.length === 1) {
                    const [pointX, pointY] = segment[0]!;
                    return (
                      <circle
                        key={segmentIndex}
                        className="perf-halo-trend-chart__series-point"
                        data-series-segment={segmentIndex}
                        cx={pointX}
                        cy={pointY}
                        r="2.5"
                      />
                    );
                  }
                  const first = segment[0]!;
                  const last = segment[segment.length - 1]!;
                  return (
                    <g key={segmentIndex} data-series-segment={segmentIndex}>
                      {item.effect === 'total' && (
                        <path
                          className="perf-halo-trend-chart__area"
                          d={`${path} L${last[0]},${zeroY} L${first[0]},${zeroY} Z`}
                        />
                      )}
                      <path className="perf-halo-trend-chart__line" d={path}/>
                    </g>
                  );
                })}
                {item.values.map((value, index) => value === 0 && (
                  <circle
                    key={orderedPoints[index]!.date}
                    className="perf-halo-trend-chart__confirmed-zero"
                    data-confirmed-zero={orderedPoints[index]!.date}
                    data-zero-series={item.effect}
                    cx={x(index)}
                    cy={zeroY}
                    r="3"
                  />
                ))}
              </g>
            ))}
          </g>

          {activePoint && activeValue !== null && activeIndex !== null && (
            <g className="perf-halo-trend-chart__selection" aria-hidden="true">
              <line x1={x(activeIndex)} x2={x(activeIndex)} y1={pad.top} y2={height - pad.bottom}/>
              {series.map((item) => {
                const value = item.values[activeIndex] ?? null;
                return value === null ? null : (
                  <circle
                    key={item.effect}
                    data-active-perf-halo-point={activePoint.date}
                    data-active-series={item.effect}
                    data-value={value}
                    cx={x(activeIndex)}
                    cy={y(value)}
                    r={item.effect === 'total' ? 4.5 : 3.5}
                  />
                );
              })}
            </g>
          )}

          <g className="perf-halo-trend-chart__hit-targets">
            {orderedPoints.map((point, index) => {
              const value = primaryValues[index] ?? null;
              const left = index === 0 ? pad.left : (x(index - 1) + x(index)) / 2;
              const right = index === orderedPoints.length - 1
                ? width - pad.right
                : (x(index) + x(index + 1)) / 2;
              const isActive = activeIndex === index;
              return (
                <g
                  key={`${point.date}-${index}`}
                  role="button"
                  tabIndex={value === null ? -1 : 0}
                  aria-disabled={value === null ? 'true' : undefined}
                  aria-pressed={isActive}
                  aria-describedby={isActive ? tooltipId : undefined}
                  aria-keyshortcuts="ArrowLeft ArrowRight Home End Escape"
                  aria-label={`${fmtDay(point.date)}. ${selectedAriaValue(point)}`}
                  data-perf-halo-point-index={index}
                  data-perf-halo-point-date={point.date}
                  onFocus={() => selectPoint(index)}
                  onPointerEnter={() => selectPoint(index)}
                  onPointerMove={() => selectPoint(index)}
                  onPointerDown={(event) => {
                    selectPoint(index);
                    if (event.pointerType !== 'touch') event.currentTarget.focus();
                  }}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                >
                  <rect
                    x={left}
                    y={pad.top}
                    width={Math.max(right - left, 1)}
                    height={innerHeight}
                  />
                </g>
              );
            })}
          </g>
        </svg>

        {!primaryValues.some((value) => value !== null) && (
          <div className="perf-halo-trend-chart__empty" role="status">
            No published days for {definition.shortLabel} in this window.
          </div>
        )}

        {activePoint && activeValue !== null && (
          <div
            id={tooltipId}
            className="perf-halo-trend-chart__tooltip"
            data-side={activeIndex !== null && activeIndex / Math.max(orderedPoints.length - 1, 1) > 0.55 ? 'left' : 'right'}
            style={tooltipStyle}
            role="tooltip"
            aria-live="polite"
          >
            <div className="perf-halo-trend-chart__tooltip-heading">
              <strong>{fmtDay(activePoint.date)}</strong>
              <span>Total · Direct · Halo</span>
            </div>
            <div className="perf-halo-trend-chart__tooltip-grid">
              {PERF_HALO_METRIC_IDS.map((tooltipMetric) => (
                <TooltipMetric
                  key={tooltipMetric}
                  metric={tooltipMetric}
                  point={activePoint}
                  selected={tooltipMetric === metric}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <figcaption className="sr-only">
        {definition.label} by ET calendar day. Confirmed zeroes remain connected; unavailable days break each line.
      </figcaption>
    </figure>
  );
}
